import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Organisation } from '../models/organisation';
import { extractErrorMessage } from '../utils/utils';
import { OrganisationSummary } from '../models/organisation-summary';
import { ContactRolesInOrg } from '../models/contact-roles-in-org';
import { SupplierSummary } from '../models/supplier-summary';
import { SimilarityList } from '../models/similarity-list';

export interface OrganisationSimilarityCheckPayload {
  id: number | null;
  name: string | null;
  acronym: string | null;
}

export interface OrganisationSimilarityRecord {
  id: number;
  name: string;
  acronym: string;
  nameSimilarity: number;
  acronymSimilarity: number;
}

@Injectable({
  providedIn: 'root',
})
export class OrganisationService {
  baseUrl: string = environment.api_url;
  constructor(private readonly http: HttpClient) { }

  checkForSimilarOrganisations(data: OrganisationSimilarityCheckPayload): Observable<SimilarityList<OrganisationSimilarityRecord>> {
    let params = new HttpParams();
    for (const key in data) {
      const value = (data as any)[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    }
    return this.http.get<SimilarityList<OrganisationSimilarityRecord>>(`${this.baseUrl}/organisations/similar`, { params }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET all organisations
  getAllOrganisations(): Observable<Organisation[]> {
    const params = new HttpParams().set('view', 'details');
    return this.http.get<Organisation[]>(`${this.baseUrl}/organisations`, { params }).pipe(
      map(list => list.map(dto => new Organisation(dto))),
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET all organisations
  getSortedOrganisations(sortBy: string): Observable<Organisation[]> {
    return this.http.get<Organisation[]>(`${this.baseUrl}/organisations?sort=${sortBy}`).pipe(
      map(list => list.map(dto => new Organisation(dto))),
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET single organisation by ID
  getOrganisationById(id: number): Observable<Organisation> {
    return this.http.get<Organisation>(`${this.baseUrl}/organisations/${id}`).pipe(
      map(dto => new Organisation(dto)),
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET all organisations summary view
  getAllOrganisationsSummary(): Observable<OrganisationSummary[]> {
    const params = new HttpParams().set('view', 'summary');
    return this.http.get<OrganisationSummary[]>(`${this.baseUrl}/organisations`, { params }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  getAllOrganisationSuppliers(): Observable<SupplierSummary[]> {
    return this.http.get<SupplierSummary[]>(`${this.baseUrl}/organisations/suppliers`,).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // Get contact roles in organisation
  getContactRolesInOrganisation(orgId: number, contactId: number): Observable<ContactRolesInOrg> {
    return this.http.get<ContactRolesInOrg>(`${this.baseUrl}/organisations/${orgId}/contact/${contactId}/roles`).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  create(organisation: Organisation): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/organisations`, organisation).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  createMinimal(organisationName: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/organisations/minimal`, { name: organisationName }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  update(organisation: Organisation): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/organisations/${organisation.id}`, organisation).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/organisations/${id}`).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  setArchivedStatus(id: number, archived: boolean): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/organisations/${id}`, { archived: archived }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }
}
